// src/components/layouts/app/sub/UserMenu.tsx
import React, { useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  User, 
  ShoppingCart, 
  Heart, 
  Package, 
  Settings, 
  LogOut, 
  ChevronDown,
  Bell,
  CreditCard,
  HelpCircle,
  Shield,
  Star,
  Gift,
  History
} from "lucide-react";
import { UserMenuItem } from "./types";

interface UserMenuProps {
  isUserMenuOpen: boolean;
  setIsUserMenuOpen: (value: boolean) => void;
  scrolled?: boolean;
}

const UserMenu: React.FC<UserMenuProps> = ({
  isUserMenuOpen,
  setIsUserMenuOpen,
  scrolled = false
}) => {
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement>(null);
  const isLoggedIn = false; // À remplacer par votre logique d'authentification

  const userMenuItems: UserMenuItem[] = [
    { icon: User, label: "Mon profil", path: "/account/profile", badge: null },
    { icon: Package, label: "Mes commandes", path: "/account/orders", badge: "3" },
    { icon: Heart, label: "Favoris", path: "/account/wishlist", badge: "12" },
    { icon: CreditCard, label: "Paiements", path: "/account/payments", badge: null },
    { icon: Star, label: "Avantages Premium", path: "/account/premium", badge: "Gold" },
    { icon: Shield, label: "Protection acheteur", path: "/protection", badge: "✓" },
    { icon: History, label: "Historique", path: "/account/history", badge: null },
    { icon: Settings, label: "Paramètres", path: "/account/settings", badge: null },
  ];

  // Fermer le menu en cliquant en dehors
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };

    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen, setIsUserMenuOpen]);

  // Fermer le menu avec la touche Échap
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isUserMenuOpen) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isUserMenuOpen, setIsUserMenuOpen]);

  return (
    <div 
      ref={menuRef}
      className={`flex items-center gap-3 transition-all duration-200 ${
        scrolled ? "gap-2.5" : "gap-3"
      }`}
    >
      {/* Notification */}
      <div className="relative">
        <button 
          className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors relative group"
          aria-label="Notifications"
        >
          <Bell className={`${scrolled ? "w-4.5 h-4.5" : "w-5 h-5"} text-gray-700`} />
          <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full border border-white"></span>
          
          {/* Tooltip notifications */}
          <div className="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-lg border border-gray-200 p-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-40">
            <div className="text-sm font-semibold text-gray-900 mb-2">Notifications</div>
            <div className="text-xs text-gray-600 mb-3">3 nouvelles notifications</div>
            <button 
              onClick={() => navigate("/notifications")}
              className="w-full text-xs text-primary hover:text-primary-dark font-medium py-2 text-center border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
            >
              Voir toutes
            </button>
          </div>
        </button>
      </div>

      {/* Favoris */}
      <Link 
        to="/wishlist" 
        className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors relative group"
        aria-label="Favoris"
      >
        <Heart className={`${scrolled ? "w-4.5 h-4.5" : "w-5 h-5"} text-gray-700 hover:text-red-500 transition-colors`} />
        <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-[9px] text-white flex items-center justify-center font-medium border border-white">
          3
        </span>
        
        {/* Tooltip favoris */}
        <div className="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-lg border border-gray-200 p-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-40">
          <div className="text-sm font-semibold text-gray-900 mb-2">Vos favoris</div>
          <div className="text-xs text-gray-600 mb-3">3 articles enregistrés</div>
          <button 
            onClick={() => navigate("/wishlist")}
            className="w-full text-xs text-primary hover:text-primary-dark font-medium py-2 text-center border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
          >
            Voir les favoris
          </button>
        </div>
      </Link>

      {/* Panier */}
      <div className="relative">
        <button 
          onClick={() => navigate("/cart")} 
          className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors relative group"
          aria-label="Panier"
        >
          <ShoppingCart className={`${scrolled ? "w-4.5 h-4.5" : "w-5 h-5"} text-gray-700 group-hover:text-primary transition-colors`} />
          <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-gradient-to-r from-primary to-primary-dark text-white text-[10px] rounded-full flex items-center justify-center font-bold shadow-sm border border-white">
            2
          </span>
        </button>
        
        {/* Tooltip panier */}
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 p-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-40">
          <div className="flex justify-between items-start mb-3">
            <div>
              <div className="text-sm font-semibold text-gray-900">Votre panier</div>
              <div className="text-xs text-gray-600">2 articles • 149,99 €</div>
            </div>
            <button 
              onClick={() => setIsUserMenuOpen(false)}
              className="text-xs text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>
          <button 
            onClick={() => {
              navigate("/cart");
              setIsUserMenuOpen(false);
            }}
            className="w-full bg-gradient-to-r from-primary to-primary-dark text-white text-sm font-medium py-2.5 rounded-md hover:shadow-md transition-all duration-200"
          >
            Voir le panier
          </button>
        </div>
      </div>

      {/* Menu utilisateur */}
      <div className="relative">
        <button
          onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
          className={`flex items-center gap-2 px-3 py-1.5 hover:bg-gray-100 rounded-lg transition-all duration-200 border ${
            isUserMenuOpen ? "bg-gray-50 border-gray-300" : "border-transparent"
          }`}
          aria-expanded={isUserMenuOpen}
          aria-label="Menu utilisateur"
        >
          <div className={`relative flex items-center justify-center rounded-full overflow-hidden ${
            scrolled ? "w-7 h-7" : "w-8 h-8"
          } bg-gradient-to-br from-blue-50 to-indigo-100 border border-gray-200`}>
            {isLoggedIn ? (
              <span className="text-xs font-bold text-indigo-700">JD</span>
            ) : (
              <User className={`${scrolled ? "w-3.5 h-3.5" : "w-4 h-4"} text-indigo-600`} />
            )}
          </div>
          
          <div className="text-left hidden lg:block">
            <div className={`font-medium text-gray-900 ${scrolled ? "text-xs" : "text-sm"}`}>
              {isLoggedIn ? "Bonjour, John" : "Mon compte"}
            </div>
            <div className={`text-gray-500 ${scrolled ? "text-[10px]" : "text-xs"}`}>
              {isLoggedIn ? "Membre Gold" : "Se connecter"}
            </div>
          </div>

          <ChevronDown className={`w-3.5 h-3.5 text-gray-500 transition-transform duration-200 ${
            isUserMenuOpen ? "rotate-180" : ""
          }`} />
        </button>

        <AnimatePresence>
          {isUserMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: 8, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 8, scale: 0.97 }}
              transition={{ duration: 0.15, ease: "easeOut" }}
              className="absolute right-0 mt-1.5 w-80 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 overflow-hidden"
              style={{ boxShadow: "0 10px 40px rgba(0, 0, 0, 0.1)" }}
            >
              {/* En-tête */}
              {!isLoggedIn ? (
                <>
                  <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
                    <div className="text-sm font-semibold text-gray-900 mb-1">
                      Bienvenue sur Kayovex
                    </div>
                    <div className="text-xs text-gray-600">
                      Connectez-vous pour accéder à vos avantages
                    </div>
                  </div>
                  
                  <div className="p-4 space-y-3">
                    <Link
                      to="/login"
                      onClick={() => setIsUserMenuOpen(false)}
                      className="block w-full bg-gradient-to-r from-primary to-primary-dark text-white text-center py-3 rounded-lg text-sm font-semibold hover:shadow-lg transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
                    >
                      Se connecter
                    </Link>
                    <Link
                      to="/register"
                      onClick={() => setIsUserMenuOpen(false)}
                      className="block w-full border border-gray-300 text-gray-900 text-center py-3 rounded-lg text-sm font-semibold hover:bg-gray-50 transition-colors hover:border-gray-400"
                    >
                      Créer un compte
                    </Link>
                  </div>
                  
                  <div className="px-4 pb-3">
                    <div className="text-xs text-gray-500 text-center">
                      En continuant, vous acceptez nos{" "}
                      <Link to="/terms" className="text-primary hover:underline">CGU</Link>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-100 to-indigo-200 rounded-full flex items-center justify-center border border-indigo-100">
                        <span className="text-sm font-bold text-indigo-700">JD</span>
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold text-gray-900">John Doe</div>
                        <div className="text-xs text-gray-600">john.doe@email.com</div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs font-medium px-2 py-1 bg-amber-100 text-amber-800 rounded-full">
                          Gold
                        </div>
                        <div className="text-xs text-gray-500 mt-1">250 pts</div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-2 max-h-80 overflow-y-auto">
                    {userMenuItems.map((item, i) => (
                      <Link
                        key={i}
                        to={item.path}
                        onClick={() => setIsUserMenuOpen(false)}
                        className="flex items-center justify-between px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors group"
                      >
                        <div className="flex items-center gap-3">
                          <div className="p-1.5 bg-gray-100 rounded-lg group-hover:bg-white group-hover:shadow-sm transition-all">
                            <item.icon className="w-4 h-4 text-gray-600" />
                          </div>
                          <span className="font-medium">{item.label}</span>
                        </div>
                        {item.badge && (
                          <span className={`text-xs px-2 py-0.5 rounded-full ${
                            item.badge === 'Gold' 
                              ? 'bg-amber-100 text-amber-800' 
                              : item.badge === '✓'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-blue-100 text-blue-800'
                          }`}>
                            {item.badge}
                          </span>
                        )}
                      </Link>
                    ))}
                  </div>
                </>
              )}

              {/* Pied de menu */}
              <div className="border-t border-gray-100 bg-gray-50">
                {isLoggedIn ? (
                  <Link
                    to="/logout"
                    onClick={() => setIsUserMenuOpen(false)}
                    className="flex items-center justify-between px-4 py-3 text-sm text-gray-700 hover:bg-red-50 hover:text-red-700 transition-colors group"
                  >
                    <div className="flex items-center gap-3">
                      <LogOut className="w-4 h-4 text-gray-600 group-hover:text-red-600" />
                      <span className="font-medium">Déconnexion</span>
                    </div>
                    <span className="text-xs text-gray-500">Session active</span>
                  </Link>
                ) : (
                  <Link
                    to="/help"
                    onClick={() => setIsUserMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                  >
                    <HelpCircle className="w-4 h-4 text-gray-600" />
                    <span className="font-medium">Aide & Support</span>
                  </Link>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default UserMenu;